import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import TextareaAutosize from 'react-autosize-textarea';
import formConfig, { canSubmitWithOnlyMessage } from '../conversation-form-config';
import { getFormActions } from '../../utils/form-helpers';
import MessagesContainer from './MessagesContainer';
import { ANONYMOUS } from '../../constants/index';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import ActivateEmail from '../../components/activate-email/ActivateEmail';
import { useUser } from '../../user/useUser';
import styles from './message-modal.module.less';
import LoginOrRegisterModal from '../../user/login-and-register-modal/LoginOrRegisterModal';
import { closeMessageModal, sendMessageAction, sendMessageToAllAction } from '../../project/project-activities-actions';
import { Modal, ModalBlock } from '@sb1/ffe-modals-react';
import { Heading2 } from '@sb1/ffe-core-react';
import { PrimaryButton } from '@sb1/ffe-buttons-react';
import { getReceivingUserFromConversation } from '../../project/project-activities-reducer';

const { setFormField, clearAll, clearAllErrors } = getFormActions(formConfig);

function getReceiverName({ sendToMultipleUsers, projectOwnerId, userId, participantName, projectOwner }) {
    if (projectOwnerId !== userId) {
        return projectOwner;
    }

    return sendToMultipleUsers ? 'alle giverne' : participantName || projectOwner || ANONYMOUS;
}

const MessageModal = ({ projectId, projectOwnerId, executeOnClose, projectOwner }) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const messageModalRef = useRef(null);

    const user = useUser();
    const messageModalOpen = useSelector((state) => state.activities.messageModalOpen);
    const isSending = useSelector((state) => state.activities.isSending);
    const messagesAndTransactions = useSelector((state) => state.activities.messagesAndTransactions);
    const participantName = useSelector((state) => state.activities.participantName);
    const error = useSelector((state) => state.activities.error);
    const receivingUserIds = useSelector((state) => state.activities.receivingUserIds);
    const conversationForm = useSelector((state) => state.conversationForm);
    const sendToAll = useSelector((state) => state.activities.sendToAll);
    const receivingUserIdFromConversation = useSelector((state) => getReceivingUserFromConversation(state));

    const isLoggedIn = !!user?.id;
    const [showLoginPopup, setShowLoginPopup] = useState(messageModalOpen && !isLoggedIn);

    useEffect(() => {
        if (!!messageModalOpen && !isLoggedIn) {
            setShowLoginPopup(true);
        }
        if (messageModalOpen && isLoggedIn) {
            messageModalRef?.current?.open();
        }
    }, [messageModalOpen]);

    const closeModal = () => {
        if (executeOnClose) {
            executeOnClose();
        }
        setShowLoginPopup(false);
        dispatch(clearAllErrors());
        dispatch(closeMessageModal());
    };

    const sendMessage = (event) => {
        event.preventDefault();

        const { inputMessage } = conversationForm;

        const userEmail = user.email;

        if (!canSubmitMessage()) {
            return;
        }

        if (sendToAll) {
            trackEvent('admin', 'message-send', { 'project-id': projectId, recipients: 'all' });
            return dispatch(
                sendMessageToAllAction({
                    message: inputMessage,
                    projectId,
                })
            ).then(() => {
                dispatch(clearAll());
                setShowLoginPopup(false);
                if (executeOnClose) {
                    executeOnClose();
                }
            });
        }

        if (!isLoggedIn) {
            return setShowLoginPopup(true);
        }
        if (projectOwnerId === user.id) {
            trackEvent('admin', 'message-send', { 'project-id': projectId, recipients: 'single' });
        } else {
            trackEvent('project', 'message-send', { 'project-id': projectId });
        }
        dispatch(
            sendMessageAction({
                message: inputMessage,
                projectId,
                receivingUserIds: receivingUserIds || receivingUserIdFromConversation,
                userEmail,
                sendToAll,
            })
        )
            .then(() => {
                dispatch(clearAll());
                setShowLoginPopup(false);

                if (executeOnClose) {
                    executeOnClose();
                }
            })
            .catch(() => {});
    };

    const canSubmitMessage = () => {
        const { inputMessage } = conversationForm;

        setFormField('inputMessage', inputMessage, true);

        return canSubmitWithOnlyMessage({ message: inputMessage }) && !isSending;
    };

    const onChangeInputMessage = (e) => {
        return dispatch(setFormField('inputMessage', e.target.value));
    };

    const { inputMessage } = conversationForm;

    const sendToMultipleUsers = sendToAll || (receivingUserIds && receivingUserIds.length) > 1;

    const receiverName = getReceiverName({
        sendToMultipleUsers,
        projectOwnerId,
        userId: user?.id,
        participantName,
        projectOwner,
    });

    const messageStep = (
        <div>
            <MessagesContainer messagesAndTransactions={messagesAndTransactions || []} userId={user.id} error={error} />
            <div className={styles.composer}>
                <form onSubmit={sendMessage}>
                    <div className={[styles.inputContainer, !user.email ? 'input-group' : ''].join(' ')} key="message">
                        <TextareaAutosize
                            disabled={isSending}
                            id="inputMessage"
                            name="inputMessage"
                            className={[styles.messageField, 'ffe-input-field'].join(' ')}
                            placeholder="Skriv meldingen her …"
                            onChange={onChangeInputMessage}
                            value={inputMessage}
                        />
                        <div className={styles.sendButton}>
                            <PrimaryButton type="submit" isLoading={isSending}>
                                Send
                            </PrimaryButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );

    const userNotActivatedStep = (
        <div>
            <ActivateEmail />
        </div>
    );

    return (
        <>
            <Modal ref={messageModalRef} onClose={closeModal} ariaLabelledby="message-modal-title">
                <ModalBlock>
                    <Heading2 id="message-modal-title">Send melding til {receiverName}</Heading2>
                    {user?.activated ? messageStep : userNotActivatedStep}
                </ModalBlock>
            </Modal>
            <LoginOrRegisterModal
                title="Logg inn eller registrer deg"
                isOpen={showLoginPopup}
                onClose={() => {
                    setShowLoginPopup(false);
                }}
                onComplete={() => {
                    trackEvent('message', 'login-or-register', {
                        'project-id': projectId,
                    });
                }}
                onHumanClose={closeModal}
                redirectLink={location.pathname}
            >
                <p>
                    Logg inn eller registrer deg for å sende melding til innsamler, så kan du få svaret tilsendt til din
                    e-post.
                </p>
            </LoginOrRegisterModal>
        </>
    );
};

export default MessageModal;
